import React from 'react';
import Registry from 'registry';

import {blackListUser, unBlackListUser} from 'reducers/member';
import paths from 'paths';

import {
    LOGIN_SUCCESS,
    LOGOUT_SUCCESS,
    SET_CURRENT_USER
} from 'reducers/currentUser';

import {
    receiveInstantNotification,
    RECEIVE_INSTANT_NOTIFICATION
} from 'reducers/memberNotificationList';

import { localizeUrl } from 'components/ReactRouterDomLocalized';
import { getCurrentUser } from 'backend-helpers/member';

let sendNotifications = undefined;

// The PM server (hitagi) authenticates the websocket by forwarding the browser's
// session cookie to /chat/neo/user/ (pm-js sends no uid/token). So PM must only be
// connected while there is a valid, activated session, and only once per user.

// uid the PM connection was opened for, null when PM is not connected
let pmUid = null;

// Session check when PM stays disconnected. Idle sockets get closed and reconnected
// regularly (proxy timeouts, nightly restart), so only check when the reconnection has
// not succeeded after a delay, and at most once per SESSION_RECHECK_INTERVAL.
// random delay: hitagi is restarted every night (cron, 05:00 UTC), dropping every
// socket at once; spread the checks so open tabs don't all hit the backend together
const SESSION_CHECK_DELAY_MIN = 30 * 1000;
const SESSION_CHECK_DELAY_JITTER = 60 * 1000;
const SESSION_RECHECK_INTERVAL = 5 * 60 * 1000;
let lastSessionCheck = 0;
let sessionCheckPending = false;
let sessionCheckTimer = null;

function scheduleSessionCheck(store) {
    if (pmUid === null || sessionCheckTimer !== null) return;

    sessionCheckTimer = setTimeout(function () {
        sessionCheckTimer = null;
        maybeCheckSession(store);
    }, SESSION_CHECK_DELAY_MIN + Math.floor(Math.random() * SESSION_CHECK_DELAY_JITTER));
}

// at most one session-lost reload per tab every 10 minutes (guards against a reload loop)
const RELOAD_GUARD_KEY = 'pm_session_lost_reload';
const RELOAD_GUARD_INTERVAL = 10 * 60 * 1000;

function reloadedRecently() {
    try {
        const now = Date.now();
        const last = parseInt(window.sessionStorage.getItem(RELOAD_GUARD_KEY), 10);
        if (last && now - last < RELOAD_GUARD_INTERVAL) return true;
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
        return false;
    } catch (e) {
        // storage unavailable: reload anyway, the page comes back as guest
        return false;
    }
}

function cancelSessionCheck() {
    if (sessionCheckTimer !== null) {
        clearTimeout(sessionCheckTimer);
        sessionCheckTimer = null;
    }
}

function canUsePm(user) {
    // same rule as BobTVUser.isLoggedIn(): /chat/neo/user/ rejects non-activated users
    return !!user && typeof user.nick !== 'undefined' && user.active === 1 && !!user.id;
}

function connectPm(store, user, url) {
    if (!canUsePm(user)) {
        disconnectPm(store);
        return;
    }

    // already connected for this user: pm-js would open a second socket and
    // orphan the first one (with its own endless reconnector)
    if (pmUid !== null && String(pmUid) === String(user.id)) return;

    disconnectPm(store);

    pmUid = user.id;
    lastSessionCheck = 0;
    store.dispatch({type: "PM_CONNECT_TO_SERVER", url: url, user: user});
}

function disconnectPm(store) {
    // pm-js crashes on PM_DISCONNECT_FROM_SERVER if it never connected
    if (pmUid === null) return;

    pmUid = null;
    cancelSessionCheck();
    store.dispatch({type: "PM_DISCONNECT_FROM_SERVER"});
}

// hitagi-js keeps retrying the login every 15s after the socket drops, even when
// the server answers "unauthorized" (session expired, logged out in another tab,
// user blocked...). When PM gets disconnected, check the session ourselves and
// stop the reconnection if it is no longer valid for the connected user.
function maybeCheckSession(store) {
    if (pmUid === null || sessionCheckPending) return;

    const now = Date.now();
    if (lastSessionCheck && now - lastSessionCheck < SESSION_RECHECK_INTERVAL) return;

    sessionCheckPending = true;
    lastSessionCheck = now;
    const checkedUid = pmUid;

    // no auth token params on purpose: hitagi only uses the session cookie
    getCurrentUser()
        .then(response => {
            sessionCheckPending = false;

            const data = (response && response.data) || {};
            const user = data.status === 'success' ? data.user : null;
            const sessionValid = canUsePm(user) && user.id == checkedUid;

            if (!sessionValid && pmUid === checkedUid) {
                disconnectPm(store);

                // PM_DISCONNECT_FROM_SERVER can't stop a reconnection that is already
                // running: hitagi-js HitagiConnector keeps its setInterval(connect, 15000)
                // and connect() re-enables autoReconnect (same for the instanoti socket).
                // The session is gone anyway, so reload: the page comes back as guest
                // (or as the user now logged in) and no timers survive.
                if (typeof window !== 'undefined' && window.location && !reloadedRecently()) {
                    window.location.reload();
                }
            }
        })
        .catch(() => {
            // network/server error: can't tell, let hitagi keep reconnecting
            sessionCheckPending = false;
        });
}

const hitagiMiddleware = function hitagiMiddleware(store) {
    return function (next) {
        return function (action) {
            const HITAGI_URL = Registry.get('HITAGI_PM_URL');
            let wss_url = HITAGI_URL.match(/^wss:/) ? HITAGI_URL : Registry.get('PUBLIC_URL').replace(/^https?:/, "wss:") + HITAGI_URL;
            

            switch(action.type) {
                case LOGIN_SUCCESS:
                case SET_CURRENT_USER: {
                    connectPm(store, action.user, wss_url);
                    break;
                }
                case LOGOUT_SUCCESS:
                    disconnectPm(store);
                    break;

                case "PM_CHANGE_CONNECTION_STATUS":
                    if (action.status === "connected") {
                        cancelSessionCheck();
                    } else if (action.status === "disconnected") {
                        scheduleSessionCheck(store);
                    }
                    break;

                case "PM_UNBLOCK_USER":
                    let action2 = unBlackListUser(action.conversation_uid);
                    store.dispatch(action2);
                    break;

                case "PM_MESSAGE":
                    if (action.message.payload) break;
                    let user = store.getState().pm.conversations[action.conversation_uid].users.recipient;
                    if (action.message.sender_uid != action.conversation_uid) break;
                    let url = paths.memberMessagePmConversationsItem.toPath({uid: user.uid});

                    url = localizeUrl(url);

                    if (url != store.getState().router.location.pathname) {
                        if (sendNotifications == undefined) {
                            sendNotifications = store.getState().currentUser.notification_settings.message.site;
                        }
                        if (sendNotifications) {
                            let image = false;
                            if (action.message.rendered.attachments) {
                                image = action.message.rendered.attachments[0].url;
                            }
                            store.dispatch(receiveInstantNotification({type: "pm_message", data: {date: 0, meta:{text: action.message.rendered.text, image: image, user: user, url: '/membre/pm/conversations/' + user.uid}}}));
                        }
                    } else {
                        if (store.getState().visibility.visibilityState == "visible") {
                            store.dispatch({type: "PM_MARK_READ", conversation_uid: action.conversation_uid});
                        }
                    }
                    break;

                case "PM_OPEN_ATTACHMENT":
                    let conv = store.getState().pm.conversations[action.conversation_uid];
                    let attachmentsList = conv.attachments;
                    let photo = null;

                    for (let i in attachmentsList) {
                        attachmentsList[i].user = conv.users[attachmentsList[i].author_label];

                        if (attachmentsList[i].url == action.attachment.url) {
                            photo = attachmentsList[i];
                        }
                    }
                    let photoList = attachmentsList;

                    if (photo) {
                        store.dispatch({type: "OPEN_PHOTO_LIST", photo:photo, list:photoList});
                        return;
                    }
                    
                    break;

                case "VISIBILITY_STATE_CHANGE":
                    if (action.visibilityState == "visible") {
                        let pm = store.getState().pm;
                        if (pm.status == "connected") {
                            for (let i in pm.conversations) {
                                if (pm.conversations[i].view.maximized) {
                                    store.dispatch({type:"PM_MARK_READ", conversation_uid: pm.conversations[i].user.uid});
                                }
                            }
                        }
                    }
                    break;
                case "SET_MEMBER_NOTIFICATIONSETTINGS_SUCCESS":
                    const settings = JSON.parse(action.data.settings);
                    
                    sendNotifications = settings.message.site;
                    break;
            }
        return next(action);
      };
    };
  };

export default hitagiMiddleware;



// WEBPACK FOOTER //
// ./src/middleware/hitagiMiddleware.js